import generateUUID from "../../../utility/generateUUID";
import Ws from "./Ws";
import SignatureManager from "./SignatureManager";
import AppSettingController from "../../Main/controller/AppSettingController";
import {NavigateFunction} from "react-router-dom";
import {get} from "lodash";
import LogController from "../../../controller/LogController";
import marketSettingLocalStorage from "../../Main/interface/MarketSettingLocalStorage";
import i18n from "../../../i18n";
import {withLang} from "../../../routes/LangLayout";

export default class PayManager {
    private readonly socketUrl: string = 'ws://localhost:2449';
    private readonly navigate: NavigateFunction;
    private readonly shopId: string;
    private readonly logController;
    private data: any = {};


    constructor(data: any, navigate: NavigateFunction, shopId: string, socketUrl?: string) {
        if (socketUrl)
            this.socketUrl = socketUrl;
        this.data = data;
        this.shopId = shopId;
        this.navigate = navigate;
        this.logController = LogController;
    }

    async pay(): Promise<{ status: boolean; data?: string }> {
        const uuid: string = generateUUID();
        const wsInstance: Ws = new Ws(this.socketUrl);
        const setting = AppSettingController.getSetting() as marketSettingLocalStorage;

        try {
            await wsInstance.connect();
            const requestPayload: string = await this.createRequestPay(this.data.amount, uuid);
            wsInstance.send(requestPayload);

            const responseData = await this.handleWebSocketResponse(wsInstance);

            this.logController.log("socket connected", "socket")
            this.logController.log("socket send message", "socket", {...JSON.parse(requestPayload)});
            this.logController.log("socket resave message", "socket", {...responseData});

            return {status: responseData.isValid, data: responseData.message};
        } catch (error) {
            wsInstance.close();
            this.navigateToErrorPage(error);
            this.logController.error("socket can not connect", "socket", {error});
            return {status: false};
        }
    }

    async payTest(): Promise<{ status: boolean; data?: string }> {
        const uuid: string = generateUUID();
        const wsInstance: Ws = new Ws(this.socketUrl);

        try {
            await wsInstance.connect();
            const requestPayload: string = await this.createRequestPay(this.data.amount, uuid);
            wsInstance.send(requestPayload);

            await this.handleWebSocketResponse(wsInstance, true);

            this.logController.log("socket-test connected", "socket-test");
            this.logController.log("socket-test send message", "socket-test", {...JSON.parse(requestPayload)});
            this.logController.log("socket-test resave message", "socket-test", {requestPayload});

            return {status: true, data: i18n.t("setting.posTestSuccess")};
        } catch (e) {
            wsInstance.close();
            this.logController.error("socket-test can not connect", "socket-test", {e});
            return {status: false, data: i18n.t("setting.posTestFailed")};
        }
    }

    private async handleWebSocketResponse(wsInstance: Ws, isTestMode?: boolean): Promise<{
        isValid: boolean;
        message?: string
    }> {
        return new Promise<{ isValid: boolean; message?: string }>((resolve) => {
            wsInstance.onMessage = async (message: string): Promise<void> => {
                const isValid: boolean = await this.validateMessage(message, isTestMode);
                wsInstance.close();
                resolve({isValid, message});
                this.logController.log("socket response message", "socket", {message});
            };

            wsInstance.onError = (error): void => {
                wsInstance.close();
                resolve({isValid: false});
                this.logController.error("socket response error", "socket", {error});
            };
        });
    }


    private async validateMessage(message: string, isTestMode?: boolean): Promise<boolean> {
        try {
            return await this.validationResponse(message, isTestMode);
        } catch {
            return false;
        }
    }

    private navigateToErrorPage(error:any): void {
        let message = i18n.t("error.posConnectionError");
        if (error?.response?.data?.error) {
            message = error.response.data.error;
        } else if (error instanceof Error) {
            message = error.message;
        } else if (error instanceof Event) {
            message = i18n.t("error.socketConnectionError");
        }

        this.navigate(withLang(`/error/${this.shopId}`), {replace: true , state: {errorMessage: message}});
    }


    private async createRequestPay(amount: number, uuid: string, payload?: string) {
        const setting = AppSettingController.getSetting()

        return JSON.stringify({
            "type": "payment_request",
            "data": {
                "entity_type": "payment_request",
                "amount": amount,
                "stan_id": uuid,
                "payload": payload,
                "sign": await SignatureManager.sign(setting.privateKey, `#${amount},${uuid},${payload ? payload : ''}#`)
            }
        })
    }

    private async validationResponse(message: string, isTestMode?: boolean) {
        const msg = JSON.parse(message);
        // Force mode
        if (msg.type === "payment_success") {
            await AppSettingController.generateBarcode()
            // send data in webHook
            this.navigate(withLang(`/success/${this.shopId}`), {replace: true});
            return true;
        } else if (msg.type === "payment_failed") {
            this.navigateToErrorPage(msg)
            return false;
        }

        const setting = AppSettingController.getSetting();

        const publicKeyPem = setting.publicKeyNetbee;
        const signature = get(msg, "data.sign", "").trim();
        const data = PayManager.getSignStructure(msg.type, msg)
        const verify = await SignatureManager.verify(publicKeyPem, signature, data)

        if (verify && !isTestMode) {
            if (msg.type === "payment_failed") {
                this.navigateToErrorPage(msg)
                return false;
            }
            if (msg.type === "payment_success") {
                await AppSettingController.generateBarcode()
                // send data in webHook
                this.navigate(withLang(`/success/${this.shopId}`), {replace: true});
                return true;
            }
        }
        if (!isTestMode) this.navigateToErrorPage(msg)
        return false;
    }

    static getSignStructure(type: "payment_failed" | "payment_success", msg: any): string {
        if (type === "payment_failed") return `#${get(msg, "data.error", "")},${get(msg, "data.stan_id", "")},#`;
        if (type === "payment_success") return `#${get(msg, "data.amount", "")},${get(msg, "data.rrn", "")},${get(msg, "data.serial", "")},${get(msg, "data.trace", "")},${get(msg, "data.card_number", "")},${get(msg, "data.datetime", "")},${get(msg, "data.stan_id", "")},#`;
        return ''
    }
}
